"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useSubscriptions } from "@/context/SubscriptionContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Subscription, Appointment } from "@/types";
import { Subscription as SubscriptionPlan } from "@/types/subscription.types";
import { subscriptionService } from "@/services/subscriptionService";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import { paymentService } from "@/services/paymentService";
import { PaginationMeta } from "@/types/appointment.api.types";
import {
  UserSubscriptionGetAllOutput,
  UserSubscriptionQueryParams,
} from "@/types/userSubscription.types";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import CommonHeader from "./common/CommonHeader";
import { SubscriptionsTable } from "./subscription/SubscriptionsTable";
import { SubscriptionDetailDialog } from "./subscription/SubscriptionDetailDialog";
import { SubscriptionConfirmDialog } from "./subscription/SubscriptionConfirmDialog";
import { AddSubscriptionDialog } from "./subscription/AddSubscriptionDialog";
import { EditSubscriptionDialog } from "./subscription/EditSubscriptionDialog";
import {
  SubscriptionFilter,
  SubscriptionFilters,
} from "./subscription/SubscriptionFilter";

/**
 * Calculate remaining services for a subscription based on appointments
 * Counts individual services (exterior, interior, dash polish) from the features used on each appointment
 */
function calculateSubscriptionServices(
  subscription: Subscription,
  appointments: Appointment[],
): {
  exteriorCleaningsUsed: number;
  interiorCleaningsUsed: number;
  dashPolishUsed: number;
  remainingServices: number;
  totalServices: number;
} {
  // Filter appointments linked to this subscription
  const subscriptionAppointments = appointments.filter(
    (apt) => apt.type === "USER_SUBSCRIPTION" && apt.typeId === subscription.id,
  );

  // Count services used from the features recorded on each appointment
  const countFeatureUsed = (namePart: string) =>
    subscriptionAppointments.filter((apt) =>
      apt.featuresUsed?.some((feature) =>
        feature.featureName.toLowerCase().includes(namePart),
      ),
    ).length;

  const exteriorCleaningsUsed = countFeatureUsed("exterior cleaning");
  const interiorCleaningsUsed = countFeatureUsed("interior cleaning");
  const dashPolishUsed = countFeatureUsed("dash polish");

  // Get totals from subscription (these come from the plan features)
  const exteriorCleaningsTotal = subscription.exteriorCleaningsTotal ?? 0;
  const interiorCleaningsTotal = subscription.interiorCleaningsTotal ?? 0;
  const dashPolishTotal = subscription.dashPolishTotal ?? 0;

  // Calculate remaining services
  const exteriorRemaining = Math.max(
    0,
    exteriorCleaningsTotal - exteriorCleaningsUsed,
  );
  const interiorRemaining = Math.max(
    0,
    interiorCleaningsTotal - interiorCleaningsUsed,
  );
  const dashPolishRemaining = Math.max(0, dashPolishTotal - dashPolishUsed);

  // Calculate totals
  const totalServices =
    exteriorCleaningsTotal + interiorCleaningsTotal + dashPolishTotal;
  const remainingServices =
    exteriorRemaining + interiorRemaining + dashPolishRemaining;

  return {
    exteriorCleaningsUsed,
    interiorCleaningsUsed,
    dashPolishUsed,
    remainingServices,
    totalServices,
  };
}

export function SubscriptionManagement() {
  const {
    subscriptions,
    customers,
    vehicles,
    appointments,
    subscriptionPlans,
    updateSubscription,
  } = useApp();

  const { createUserSubscription, refreshSubscriptions } = useSubscriptions();

  const [activeTab, setActiveTab] = useState("all");
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] =
    useState<Subscription | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    "expire" | "activate" | null
  >(null);
  const [autoRenewUpdatingIds, setAutoRenewUpdatingIds] = useState<string[]>(
    [],
  );

  // Filter state
  const [filters, setFilters] = useState<SubscriptionFilters>({
    search: "",
    planId: "all",
    startDate: "",
    endDate: "",
  });

  const [queryParams, setQueryParams] = useState<UserSubscriptionQueryParams>({
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [paginatedSubscriptions, setPaginatedSubscriptions] = useState<
    Subscription[]
  >([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Calculate services for all subscriptions based on appointments
  const subscriptionsWithCalculatedServices = useMemo(() => {
    return subscriptions.map((subscription) => {
      const calculated = calculateSubscriptionServices(
        subscription,
        appointments,
      );
      // Preserve original totals from subscription plan, use calculated totals as fallback
      const exteriorTotal = subscription.exteriorCleaningsTotal || 0;
      const interiorTotal = subscription.interiorCleaningsTotal || 0;
      const dashPolishTotal = subscription.dashPolishTotal || 0;
      const totalServices =
        exteriorTotal + interiorTotal + dashPolishTotal ||
        calculated.totalServices;

      return {
        ...subscription,
        exteriorCleaningsUsed: calculated.exteriorCleaningsUsed,
        interiorCleaningsUsed: calculated.interiorCleaningsUsed,
        dashPolishUsed: calculated.dashPolishUsed,
        exteriorCleaningsTotal: exteriorTotal,
        interiorCleaningsTotal: interiorTotal,
        dashPolishTotal: dashPolishTotal,
        remainingServices: calculated.remainingServices,
        totalServices: totalServices,
      };
    });
  }, [subscriptions, appointments]);

  const mapApiSubscriptionToViewModel = useCallback((
    sub: UserSubscriptionGetAllOutput,
  ): Subscription => {
    const firstRule = sub.rules?.[0];
    const secondRule = sub.rules?.[1];
    const computedStatus = sub.isActive ? "active" : "expired";

    return {
      id: sub.id,
      subscriptionNumber: sub.subscriptionNumber ?? null,
      customerId: sub.customerId,
      vehicleId: sub.vehicleId || "",
      planId: sub.subscriptionId,
      planName: sub.subscription?.name || "Unknown Plan",
      monthlyPrice: sub.subscription?.price || 0,
      startDate: new Date(sub.cycleStartDate || sub.createdAt),
      cycleEndDate: sub.cycleEndDate ? new Date(sub.cycleEndDate) : null,
      recurringDay: firstRule?.day || "-",
      preferredTime: firstRule?.time || "-",
      recurringDay2: secondRule?.day,
      preferredTime2: secondRule?.time,
      status: sub.status || computedStatus,
      isActive: sub.isActive,
      autoRenew: sub.autoRenew ?? false,
      remainingServices: 0,
      totalServices: 0,
      exteriorCleaningsUsed: 0,
      exteriorCleaningsTotal: 0,
      interiorCleaningsUsed: 0,
      interiorCleaningsTotal: 0,
      dashPolishUsed: 0,
      dashPolishTotal: 0,
      customerName: sub.customer?.name,
      customerPhone: sub.customer?.phone || undefined,
      vehicleMake: sub.vehicle?.make,
      vehicleModel: sub.vehicle?.model,
      vehiclePlateNumber: sub.vehicle?.plateNumber || undefined,
    };
  }, []);

  const refreshPaginatedSubscriptions = useCallback(async () => {
    try {
      const response = await userSubscriptionService.getUserSubscriptionsPaginated(
        queryParams,
      );
      const mapped = response.data.map(mapApiSubscriptionToViewModel);

      // Fetch payment info for each subscription on the current page to display due/adjustment
      await Promise.all(
        mapped.map(async (sub, idx) => {
          try {
            const payment = await paymentService.getPaymentByRefId(sub.id);
            if (payment) {
              mapped[idx] = {
                ...sub,
                paymentDueAmount: payment.dueAmount,
                paymentDiscount: payment.discount,
                paymentPlatinumDiscount: payment.platinumDiscount,
              };
            }
          } catch (err) {
            // ignore individual fetch errors
          }
        }),
      );

      setPaginatedSubscriptions(mapped);
      setPagination(response.pagination);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch subscriptions";
      console.error("Error fetching subscriptions:", error);
      toast.error(errorMessage);
    }
  }, [queryParams, mapApiSubscriptionToViewModel]);

  // Reset pagination when filters or active tab change
  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: filters.search || undefined,
      planId: filters.planId !== "all" ? filters.planId : undefined,
      status:
        activeTab !== "all"
          ? (activeTab as "active" | "expired")
          : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    }));
  }, [filters, activeTab]);

  useEffect(() => {
    refreshPaginatedSubscriptions();
  }, [refreshPaginatedSubscriptions]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: "bg-green-500 hover:bg-green-600",
      expired: "bg-gray-500 hover:bg-gray-600",
    };

    return (
      <Badge className={colors[status] || ""}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handleAction = (
    subscription: Subscription,
    action: "expire" | "activate",
  ) => {
    setSelectedSubscription(subscription);
    setConfirmAction(action);
    setIsConfirmDialogOpen(true);
  };

  const confirmActionHandler = async () => {
    if (!selectedSubscription || !confirmAction) return;

    try {
      if (confirmAction === "expire") {
        await userSubscriptionService.expireUserSubscription(selectedSubscription.id);
      } else if (confirmAction === "activate") {
        await userSubscriptionService.reactivateUserSubscription(
          selectedSubscription.id,
        );
      }

      // Refresh subscriptions to get updated data from backend
      await refreshSubscriptions();
      await refreshPaginatedSubscriptions();
      const actionLabel = confirmAction === "expire" ? "expired" : "activated";
      toast.success(`Subscription ${actionLabel} successfully`);
      setIsConfirmDialogOpen(false);
      setConfirmAction(null);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${confirmAction === "expire" ? "expire" : "activate"} subscription`;
      console.error(`Error performing subscription action:`, error);
      toast.error(errorMessage);
    }
  };

  const handleToggleAutoRenew = async (
    subscription: Subscription,
    autoRenew: boolean,
  ) => {
    setAutoRenewUpdatingIds((prev) => [...prev, subscription.id]);

    try {
      await userSubscriptionService.setUserSubscriptionAutoRenew(
        subscription.id,
        autoRenew,
      );

      setPaginatedSubscriptions((prev) =>
        prev.map((sub) =>
          sub.id === subscription.id ? { ...sub, autoRenew } : sub,
        ),
      );
      toast.success(`Re-subscribe turned ${autoRenew ? "on" : "off"}`);

      // Keep the context list (used by the detail dialog) in sync
      await refreshSubscriptions();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update re-subscribe";
      console.error("Error updating re-subscribe:", error);
      toast.error(errorMessage);
    } finally {
      setAutoRenewUpdatingIds((prev) =>
        prev.filter((id) => id !== subscription.id),
      );
    }
  };

  const viewDetails = (subscription: Subscription) => {
    // Find the subscription with calculated services
    const calculatedSubscription =
      subscriptionsWithCalculatedServices.find(
        (sub) => sub.id === subscription.id,
      ) || subscription;
    setSelectedSubscription(calculatedSubscription);
    setIsDetailDialogOpen(true);
  };

  const handleEdit = (subscription: Subscription) => {
    // Find the subscription with calculated services
    const calculatedSubscription =
      subscriptionsWithCalculatedServices.find(
        (sub) => sub.id === subscription.id,
      ) || subscription;
    setEditingSubscription(calculatedSubscription);
    setIsEditDialogOpen(true);
  };

  const handleCreateSubscription = async (
    formData: any,
    planFeatures?: any,
  ) => {
    try {
      await createUserSubscription(formData, planFeatures);
      await refreshSubscriptions();
      setIsAddDialogOpen(false);

      if (queryParams.page === 1) {
        await refreshPaginatedSubscriptions();
      } else {
        setQueryParams((prev) => ({ ...prev, page: 1 }));
      }
    } catch (error) {
      // Error is already handled in createUserSubscription
    }
  };

  // Get subscription plans from API (these are the templates)
  const [subscriptionPlanTemplates, setSubscriptionPlanTemplates] = useState<
    SubscriptionPlan[]
  >([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plans = await subscriptionService.getAllSubscriptions();
        setSubscriptionPlanTemplates(plans);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <CommonHeader
        title="Subscription Management"
        description="Manage recurring subscriptions"
        onClick={() => setIsAddDialogOpen(true)}
        buttonText="Add Subscription"
        buttonIcon={Plus}
      />

      {/* Filters */}
      <SubscriptionFilter
        filters={filters}
        onFilterChange={setFilters}
        plans={subscriptionPlanTemplates}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="expired">Expired</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {/* Subscriptions Table */}
          <SubscriptionsTable
            subscriptions={paginatedSubscriptions}
            customers={customers}
            vehicles={vehicles}
            getStatusBadge={getStatusBadge}
            onView={viewDetails}
            onEdit={handleEdit}
            onExpire={(sub) => handleAction(sub, "expire")}
            onActivate={(sub) => handleAction(sub, "activate")}
            onToggleAutoRenew={handleToggleAutoRenew}
            autoRenewUpdatingIds={autoRenewUpdatingIds}
            query={{ page: queryParams.page || 1, limit: queryParams.limit || 10 }}
            pagination={pagination}
            onQueryChange={(query) =>
              setQueryParams((prev) => ({ ...prev, ...query }))
            }
          />
        </TabsContent>
      </Tabs>

      {/* Add Subscription Dialog */}
      <AddSubscriptionDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        customers={customers}
        vehicles={vehicles}
        subscriptionPlans={subscriptionPlanTemplates}
        onCreate={handleCreateSubscription}
      />

      {/* Detail Dialog */}
      <SubscriptionDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        subscription={selectedSubscription}
        customers={customers}
        vehicles={vehicles}
        appointments={appointments}
        getStatusBadge={getStatusBadge}
        onEdit={handleEdit}
      />

      {/* Confirm Action Dialog */}
      <SubscriptionConfirmDialog
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
        action={confirmAction}
        onConfirm={confirmActionHandler}
      />

      {/* Edit Subscription Dialog */}
      <EditSubscriptionDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        subscription={editingSubscription}
        vehicles={vehicles}
        onRefreshPaginated={refreshPaginatedSubscriptions}
      />
    </div>
  );
}
